import { useCallback, useRef, useState } from 'react';
import { MatchState } from '../types';
import {
  FeedbackAnswer,
  generateFeedbackJSON,
  sendResultsToCallback,
  SendStatus,
} from './useResultSender';
import { SendError, classifySendError } from '../errors/sendError';

export interface UseFeedbackSenderReturn {
  sendStatus: SendStatus;
  lastError: SendError | null;
  hasCallbackUrl: boolean;
  sendFeedback: (answers: FeedbackAnswer[]) => Promise<void>;
  retry: () => Promise<void>;
}

/**
 * Hook d'envoi du questionnaire post-match.
 *
 * Indépendant de useResultSender pour ne pas mélanger les deux cycles de vie
 * (validation du résultat vs soumission du questionnaire).
 */
export function useFeedbackSender(matchState: MatchState): UseFeedbackSenderReturn {
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [lastError, setLastError] = useState<SendError | null>(null);
  const lastAnswers = useRef<FeedbackAnswer[] | null>(null);

  const hasCallbackUrl = Boolean(matchState.matchInfo.resultCallbackUrl);

  const sendFeedback = useCallback(
    async (answers: FeedbackAnswer[]) => {
      lastAnswers.current = answers;

      if (!hasCallbackUrl || !matchState.matchInfo.resultCallbackUrl) {
        // Erreur de config, pas réseau : on la classe en `unknown` pour réutiliser
        // le même rendu et message générique.
        setSendStatus('error');
        setLastError({ kind: 'unknown', technical: 'No callback URL configured' });
        return;
      }

      setSendStatus('sending');
      setLastError(null);

      try {
        const payload = generateFeedbackJSON(matchState, answers);
        await sendResultsToCallback(payload, matchState.matchInfo.resultCallbackUrl);
        setSendStatus('success');
      } catch (error) {
        setSendStatus('error');
        setLastError(classifySendError(error));
      }
    },
    [hasCallbackUrl, matchState]
  );

  const retry = useCallback(async () => {
    if (lastAnswers.current === null) {
      return;
    }
    await sendFeedback(lastAnswers.current);
  }, [sendFeedback]);

  return {
    sendStatus,
    lastError,
    hasCallbackUrl,
    sendFeedback,
    retry,
  };
}
